import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import AdminFilterBar from './components/AdminFilterBar';
import MainButton from '../../../shared/layout/buttons/MainButton';
import FreshnessBadge from './components/FreshnessBadge';
import MetricTrustBadge from './components/MetricTrustBadge';
import AdminDataTable from './components/AdminDataTable';
import { fetchAdminRevenueSummary, fetchAdminRevenueByOrganization, fetchAdminRevenueByIntegration } from '../../../services/Http/AdminApi';
import { useCurrency } from '../../../context/CurrencyProvider';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';

const AdminRevenue = () => {
    const { formatEurAmount } = useCurrency();
    const [period, setPeriod] = useState('30d');
    const [summary, setSummary] = useState(null);
    const [byOrg, setByOrg] = useState([]);
    const [byIntegration, setByIntegration] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [summaryResp, orgResp, intResp] = await Promise.all([
                fetchAdminRevenueSummary({ period }),
                fetchAdminRevenueByOrganization({ period }),
                fetchAdminRevenueByIntegration({ period }),
            ]);
            setSummary(summaryResp?.data || null);
            setByOrg(orgResp?.data || []);
            setByIntegration(intResp?.data || []);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to load revenue analytics');
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => { load(); }, [load]);

    return (
        <PageContainer>
            <PageHeader><PageTitle>Revenue Analytics</PageTitle></PageHeader>
            <PageContent>
                <AdminFilterBar>
                    <FilterSide>
                        <PeriodSelect value={period} onChange={(e) => setPeriod(e.target.value)}>
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                        </PeriodSelect>
                        <FreshnessBadge status="success" sourceType="operational" />
                    </FilterSide>
                    <MainButton size="small" onClick={load}>Refresh</MainButton>
                </AdminFilterBar>

                <StatsGrid>
                    {renderRevenueCard('Total Revenue', summary?.total_revenue, 'revenueValue', formatEurAmount)}
                    {renderRevenueCard('Transaction Fee Revenue', summary?.transaction_fee_revenue, 'info', formatEurAmount)}
                    {renderRevenueCard('Cross-sell Commission', summary?.cross_sell_commission_revenue, 'success', formatEurAmount)}
                    {renderRevenueCard('Billed Amount', summary?.billed_amount, 'warning', formatEurAmount)}
                    {renderRevenueCard('Invoiced Amount', summary?.invoiced_amount, 'transactions', formatEurAmount)}
                    {renderRevenueCard('GMV', summary?.gmv, 'totalVolume', formatEurAmount)}
                </StatsGrid>

                <Section>
                    <SectionTitle>Revenue by Organization</SectionTitle>
                    <AdminDataTable
                        loading={loading}
                        rows={byOrg}
                        emptyText="No organization revenue data"
                        columns={[
                            { key: 'organization_name', label: 'Organization' },
                            { key: 'transactions_count', label: 'Transactions' },
                            { key: 'gmv', label: 'GMV', render: (r) => formatEurAmount(r.gmv) },
                            { key: 'revenue', label: 'Revenue', render: (r) => formatEurAmount(r.revenue) },
                            { key: 'transaction_fee_amount', label: 'Fees', render: (r) => formatEurAmount(r.transaction_fee_amount) },
                        ]}
                    />
                </Section>

                <Section>
                    <SectionTitle>Revenue by Integration (Proxy)</SectionTitle>
                    <AdminDataTable
                        loading={loading}
                        rows={byIntegration}
                        emptyText="No integration revenue data"
                        columns={[
                            { key: 'source', label: 'Source' },
                            { key: 'transactions_count', label: 'Transactions' },
                            { key: 'gmv', label: 'GMV', render: (r) => formatEurAmount(r.gmv) },
                        ]}
                    />
                    <SmallNote>First pass uses payment method as integration source proxy where direct source attribution is not persisted consistently.</SmallNote>
                </Section>
            </PageContent>
        </PageContainer>
    );
};

export default AdminRevenue;

const renderRevenueCard = (title, metric, color, formatEurAmount) => (
    <MetricCard key={title}>
        <MetaRow>
            <MetricTrustBadge tier={metric?.trust_tier || 'derived'} />
        </MetaRow>
        <StatCard icon="¤" title={title} value={formatEurAmount(metric?.value)} color={color} />
    </MetricCard>
);

const FilterSide = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
`;
const PeriodSelect = styled(SimpleSelect)`
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 10px;
    padding: 0.5625rem 0.75rem;
    background: ${({ theme }) => theme.colors.surface || '#fff'};
`;
const MetricCard = styled.div`display:flex;flex-direction:column;gap: 0.5rem;`;
const MetaRow = styled.div`display:flex;justify-content:flex-end;`;
const Section = styled.section`margin-top: 1.25rem;`;
const SectionTitle = styled.h3`font-size:1rem;margin: 0 0 0.75rem;color:${({ theme }) => theme.colors.textPrimary || '#111827'};`;
const SmallNote = styled.p`margin: 0.5rem 0 0;color:${({ theme }) => theme.colors.textMuted || '#6b7280'};font-size: 0.75rem;`;
